/* eslint-disable react/prop-types */
import React, { useState, useContext } from 'react';
import { Link } from 'gatsby';
import Heart from './icons/Heart';
import Close from './icons/Close';
import Trash from './icons/Trash';
import reduce from 'lodash/reduce';
import { m as motion, AnimatePresence } from 'framer-motion';

import Context from '../context/Context';

import useIsLoggedIn from '../hooks/useIsLoggedIn';
import { useLocation } from '@reach/router';
import cx from 'classnames';

const AddToWishList = ({ pdp, variantId }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isNewWishListModalOpen, setIsNewWishListModalOpen] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const { customerEmail, customerWishLists, setCustomerWishLists } =
    useContext(Context);

  const isLoggedIn = useIsLoggedIn();

  const { pathname } = useLocation();

  const isProductInWishList = reduce(
    customerWishLists,
    (acc, wishlist) => {
      if (acc) return true;

      return !!wishlist?.list?.find(
        listItem => listItem.variantId === variantId
      );
    },
    false
  );

  const addToExistingWishList = async wishlistId => {
    setIsLoading(true);

    try {
      const request = await fetch('/api/wishlist-add', {
        method: 'POST',
        body: JSON.stringify({ wishlistId, variantId }),
      });

      await request.json();

      setIsSuccess(true);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const createNewWishListAndAdd = async e => {
    e.preventDefault();

    const { target } = e;

    const name = target.name.value;

    if (!name) {
      return;
    }

    setIsLoading(true);
    try {
      const request = await fetch('/api/wishlist-new', {
        method: 'POST',
        body: JSON.stringify({ name, variantId, email: customerEmail }),
      });

      const { createdWishList } = await request.json();

      const updatedWishLists = [...customerWishLists, createdWishList];
      setCustomerWishLists(updatedWishLists);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
      setIsNewWishListModalOpen(false);
    }
  };

  const removeFromWishlist = async () => {
    const shouldDelete = await confirm('Are you sure you want to delete?');

    if (!shouldDelete) {
      return;
    }

    const wishlistId = pathname.split('/wishlists/')[1].replace('/', '');

    setIsLoading(true);

    try {
      await fetch('/api/wishlist-remove', {
        method: 'POST',
        body: JSON.stringify({ variantId, wishlistId }),
      });
    } catch (error) {
      console.error(error);
    } finally {
      window.location.reload();
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setIsSuccess(false);
    setIsLoading(false);
  };

  const handleModalClick = () => {
    setIsModalOpen(!isModalOpen);
  };

  const unauthenticatedElement = !isLoggedIn && !isLoading && (
    <p>
      <Link to="/account" className="link--underline">
        Sign In
      </Link>{' '}
      to add to your wish list.
    </p>
  );

  const newWishlistButton = (
    <div className="relative">
      <button
        onClick={() => setIsNewWishListModalOpen(true)}
        className="link--underline"
      >
        Create New Wishlist
      </button>
      <AnimatePresence mode="wait">
        {isNewWishListModalOpen && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.2 }}
            className="absolute top-0 -left-20 -right-20 w-fit h-full z-10"
          >
            <form
              onSubmit={createNewWishListAndAdd}
              className="flex items-center p-5 border border-solid bg-warm"
            >
              <label>
                <input type="text" name="name" />
              </label>
              <button className="bg-warm" type="submit">
                Create
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );

  const authenticatedWrapper = isLoggedIn && !isSuccess && !isLoading && (
    <div className="flex flex-col gap-10">
      {customerWishLists.length < 1 && <p>No wish lists Found.</p>}
      {customerWishLists.length > 0 && (
        <React.Fragment>
          <p className="serif--sm">Your wish lists</p>
          <ul className="flex flex-col gap-5">
            {customerWishLists.map(({ _id, title }) => (
              <li key={_id}>
                <button
                  title={`Add to ${title}`}
                  onClick={() => addToExistingWishList(_id)}
                  className="w-fit text-left link--underline-reverse"
                >
                  {title}
                </button>
              </li>
            ))}
          </ul>
        </React.Fragment>
      )}
      {newWishlistButton}
    </div>
  );

  const loadingElement = isLoading && !isSuccess && <p>Loading...</p>;

  const successElement = isSuccess && !isLoading && <p>Added to Wishlist!</p>;

  const addToWishlistElement = !pathname.includes('wishlists') && (
    <div className="relative">
      <button
        onClick={() => {
          if (!isProductInWishList) handleModalClick();
        }}
        disabled={isLoading}
        title={
          isProductInWishList
            ? 'You have this item saved'
            : isLoggedIn
            ? 'Add to wish list'
            : 'Sign In'
        }
        className={cx(
          'transition-colors duration-300  hover:text-dark-brown  bg-warm p-5',
          {
            'text-dark-brown': isProductInWishList,
            'text-warm': !isProductInWishList,
            'cursor-pointer': !isProductInWishList,
          }
        )}
      >
        <Heart className="w-12 h-10" />
      </button>
      <AnimatePresence mode="wait">
        {isModalOpen && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.2 }}
            className="absolute bg-warm text-brown top-0 right-0 border border-dark-brown border-solid p-10 pr-20 w-max"
          >
            {!isLoading && (
              <button className="absolute top-5 right-5" onClick={closeModal}>
                <Close className="w-12 h-12" />
              </button>
            )}
            {successElement}
            {loadingElement}
            {unauthenticatedElement}
            {authenticatedWrapper}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );

  const removeFromWishListElement = pathname.includes('wishlists') &&
    isLoggedIn && (
      <button
        onClick={removeFromWishlist}
        disabled={isLoading}
        className="transition-opacity duration-300 hover:opacity-50 p-5"
      >
        <Trash className="w-auto h-10" />
      </button>
    );

  return (
    <div
      className={cx('', {
        'absolute top-0 right-0 z-3 border-l border-b border-solid bg-mauve ':
          !pdp,
      })}
    >
      {addToWishlistElement}
      {removeFromWishListElement}
    </div>
  );
};

export default AddToWishList;
